import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StyleSheet, View, Text, Pressable, ActivityIndicator } from "react-native";
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import CampPageReviews from "./CampPageReviews";
import ReviewDAO from "../../../../data/ReviewDAO";
import { CampReviewEligibility, CampReviewRecord, CampReviewSummary } from "../../../../interface/Review";
import CampReviewComposerModal from "./CampReviewComposerModal";
import { toast } from "react-toastify";
import { isParentAccount } from "../../../../interface/AccountType";

interface CampPageReviewListViewProps {
    campId: string;
    currentUser?: any;
    onSummaryChange?: (summary: CampReviewSummary) => void;
}

const CampPageReviewListView: React.FC<CampPageReviewListViewProps> = ({
    campId,
    currentUser,
    onSummaryChange,
}) => {
    const [reviews, setReviews] = useState<CampReviewRecord[]>([]);
    const [summary, setSummary] = useState<CampReviewSummary>({ averageRating: 0, reviewCount: 0, ratingBreakdown: {} });
    const [eligibility, setEligibility] = useState<CampReviewEligibility>({ eligible: false, reasons: [], eligibleSessions: [] });
    const [isLoading, setIsLoading] = useState(true);
    const [showComposer, setShowComposer] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const onSummaryChangeRef = useRef(onSummaryChange);

    const isFamilyUser = isParentAccount(currentUser?.accountType);

    useEffect(() => {
        onSummaryChangeRef.current = onSummaryChange;
    }, [onSummaryChange]);

    const loadReviewsAndEligibility = useCallback(async () => {
        if (!campId) {
            setReviews([]);
            setSummary({ averageRating: 0, reviewCount: 0, ratingBreakdown: {} });
            setEligibility({ eligible: false, reasons: [], eligibleSessions: [] });
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            const [reviewResponse, eligibilityResponse] = await Promise.all([
                ReviewDAO.getCampReviews(campId),
                ReviewDAO.getReviewEligibility(campId, currentUser?.userId),
            ]);
            const safeSummary = reviewResponse.summary || { averageRating: 0, reviewCount: 0, ratingBreakdown: {} };
            const safeReviews = Array.isArray(reviewResponse.reviews) ? reviewResponse.reviews : [];
            setReviews(safeReviews);
            setSummary(safeSummary);
            setEligibility(eligibilityResponse);
            onSummaryChangeRef.current?.(safeSummary);
        } catch (error) {
            console.error('Failed to load camp reviews:', error);
            // Fail gracefully to empty state instead of spinning.
            setReviews([]);
            setSummary({ averageRating: 0, reviewCount: 0, ratingBreakdown: {} });
            setEligibility({ eligible: false, reasons: ['Reviews are currently unavailable.'], eligibleSessions: [] });
        } finally {
            setIsLoading(false);
        }
    }, [campId, currentUser?.userId]);

    useEffect(() => {
        loadReviewsAndEligibility();
    }, [loadReviewsAndEligibility]);

    const ratingLabel = useMemo(() => {
        if (!summary.reviewCount) return 'No ratings yet';
        return `${summary.averageRating.toFixed(1)} (${summary.reviewCount} ${summary.reviewCount === 1 ? 'review' : 'reviews'})`;
    }, [summary.averageRating, summary.reviewCount]);

    const openComposer = () => {
        if (!currentUser?.userId) {
            toast.info('Please log in to leave a review.');
            return;
        }
        if (!eligibility.eligible) {
            toast.info(eligibility.reasons?.[0] || 'You can review after attending a session.');
            return;
        }
        setShowComposer(true);
    };

    const handleSubmitReview = async (payload: { sessionId: string; rating: number; comment: string }) => {
        if (!currentUser?.userId) return;
        setIsSubmitting(true);
        try {
            const result = await ReviewDAO.submitCampReview({
                campId,
                userId: currentUser.userId,
                sessionId: payload.sessionId,
                rating: payload.rating,
                comment: payload.comment,
            });

            if (!result.success) {
                toast.error(result.error || 'Unable to submit review.');
                return;
            }

            toast.success('Thanks for sharing your experience!');
            setShowComposer(false);
            await loadReviewsAndEligibility();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <View style={styles.headerRow}>
                <View>
                    <Text style={styles.title}>Reviews</Text>
                    <View style={styles.summaryRow}>
                        <MaterialCommunityIcons name="star" size={16} color="#F5A623" />
                        <Text style={styles.summaryText}>{ratingLabel}</Text>
                    </View>
                </View>
                {isFamilyUser ? (
                    <Pressable
                        onPress={openComposer}
                        style={[styles.writeButton, !eligibility.eligible && styles.writeButtonDisabled]}
                    >
                        <MaterialCommunityIcons name="pencil-outline" size={16} color={eligibility.eligible ? "#1D4ED8" : "#9CA3AF"} />
                        <Text style={[styles.writeButtonText, !eligibility.eligible && styles.writeButtonTextDisabled]}>
                            Write a review
                        </Text>
                    </Pressable>
                ) : null}
            </View>

            {isLoading ? (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="small" color="#2563EB" />
                    <Text style={styles.loadingText}>Loading reviews...</Text>
                </View>
            ) : reviews.length === 0 ? (
                <View style={styles.emptyState}>
                    <MaterialCommunityIcons name="message-text-outline" size={24} color="#9CA3AF" />
                    <Text style={styles.emptyStateTitle}>No reviews yet</Text>
                    <Text style={styles.emptyStateBody}>
                        Families who complete a session can leave detailed feedback here.
                    </Text>
                </View>
            ) : (
                <View style={styles.container}>
                    {reviews.slice(0, 6).map((review) => (
                        <CampPageReviews
                            key={review.reviewId}
                            review={review}
                        />
                    ))}
                </View>
            )}

            <CampReviewComposerModal
                visible={showComposer}
                onClose={() => setShowComposer(false)}
                onSubmit={handleSubmitReview}
                eligibleSessions={eligibility.eligibleSessions || []}
                isSubmitting={isSubmitting}
            />
        </>
    );
};

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
    },
    headerRow: {
        marginTop: 30,
        marginBottom: 20,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
    },
    title: {
        fontSize: 20,
        fontWeight: '500',
        marginBottom: 6,
    },
    summaryRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    summaryText: {
        marginLeft: 6,
        fontSize: 14,
        color: '#4B5563',
        fontWeight: '500',
    },
    writeButton: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: '#BFDBFE',
        backgroundColor: '#EFF6FF',
        borderRadius: 999,
        paddingHorizontal: 14,
        paddingVertical: 8,
    },
    writeButtonDisabled: {
        borderColor: '#E5E7EB',
        backgroundColor: '#F9FAFB',
    },
    writeButtonText: {
        marginLeft: 6,
        fontSize: 13,
        color: '#1D4ED8',
        fontWeight: '600',
    },
    writeButtonTextDisabled: {
        color: '#9CA3AF',
    },
    loadingContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 16,
    },
    loadingText: {
        marginLeft: 8,
        color: '#6B7280',
        fontSize: 14,
    },
    emptyState: {
        paddingVertical: 18,
        alignItems: 'flex-start',
    },
    emptyStateTitle: {
        marginTop: 8,
        fontSize: 16,
        color: '#111827',
        fontWeight: '600',
    },
    emptyStateBody: {
        marginTop: 4,
        color: '#6B7280',
        fontSize: 14,
    },
});

export default CampPageReviewListView;